import { useEffect, useState, useCallback } from "react";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { livestockById } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Trash2, LineChart as LineIcon, TrendingUp, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";

export default function Yields() {
  const { settings, fmt } = useFarm();
  const active = livestockById(settings.active_livestock);
  const [logs, setLogs] = useState([]);
  const [trend, setTrend] = useState({ metrics: [], series: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    metric: active.metrics[0], value: 0, unit: "", unit_price: 0, date: new Date().toISOString().slice(0, 10), notes: "",
  });
  const postIncome = async () => {
    let res;
    try { res = await api.postYieldIncome(); } catch { return toast.error("Could not update expected income. Please try again."); }
    toast.success(res.posted ? `Updated ${res.posted} weekly expected income entr${res.posted === 1 ? "y" : "ies"}` : "All priced yields are already in Expected Income");
    load();
  };

  const load = useCallback(async () => {
    const all = await api.getYields();
    const mine = all.filter((y) => y.livestock_type === settings.active_livestock);
    setLogs([...mine].sort((a, b) => (a.date < b.date ? 1 : -1)));
    const metrics = [];
    const byDate = {};
    [...mine].sort((a, b) => (a.date < b.date ? -1 : 1)).forEach((y) => {
      if (!metrics.includes(y.metric)) metrics.push(y.metric);
      const e = byDate[y.date] || (byDate[y.date] = { date: y.date });
      e[y.metric] = Math.round(((e[y.metric] || 0) + y.value) * 100) / 100;
    });
    const series = Object.keys(byDate).sort().map((k) => byDate[k]);
    setTrend({ metrics, series });
  }, [settings.active_livestock]);

  useEffect(() => {
    load();
    setForm((f) => ({ ...f, metric: active.metrics[0] }));
  }, [load, active.metrics]);

  const submit = async () => {
    if (!form.metric || form.value === "" ) return toast.error("Metric and value required");
    await api.createYield({ ...form, livestock_type: settings.active_livestock, value: parseFloat(form.value) || 0, unit_price: parseFloat(form.unit_price) || 0 });
    toast.success("Yield logged");
    setOpen(false);
    setForm({ metric: active.metrics[0], value: 0, unit: "", unit_price: 0, date: new Date().toISOString().slice(0, 10), notes: "" });
    load();
  };
  const remove = async (id) => { await api.deleteYield(id); load(); toast.success("Deleted"); };

  const trendData = trend.series.map((s) => ({
    ...s,
    label: new Date(s.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Yield Tracking</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Production output for <span className="capitalize font-medium text-foreground">{active.name}</span> — monitor changes over time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="h-10 rounded-full gap-1.5" onClick={postIncome} data-testid="post-yield-income-button"><RefreshCw className="h-4 w-4" /> Update Expected Income</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 rounded-full gap-1.5" data-testid="add-yield-button"><Plus className="h-4 w-4" /> Log Yield</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Log Production Yield</DialogTitle>
              <DialogDescription>Record a production metric for {active.name}.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Metric</Label>
                <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="yield-metric-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{active.metrics.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Value</Label>
                  <Input type="number" className="mt-1.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="yield-value-input" />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input className="mt-1.5" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="L, kg, crates" data-testid="yield-unit-input" />
                </div>
              </div>
              <div>
                <Label>Price per unit ({settings.currency_symbol})</Label>
                <Input type="number" step="0.01" className="mt-1.5" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="yield-price-input" />
                {parseFloat(form.value) > 0 && parseFloat(form.unit_price) > 0 && (
                  <p className="mt-1.5 text-xs text-[hsl(var(--emerald))]" data-testid="yield-income-preview">{fmt(form.value * form.unit_price)} goes into this week's Expected Income (updated every Monday).</p>
                )}
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="yield-date-input" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="yield-notes-input" />
              </div>
            </div>
            <DialogFooter><Button className="rounded-full" onClick={submit} data-testid="save-yield-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-6" data-testid="yield-trend-chart">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-5 w-5 text-accent" />
          <h2 className="font-display text-xl font-bold tracking-tight">Output Trend</h2>
        </div>
        {trendData.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={trendData} margin={{ left: -8, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={50} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {trend.metrics.map((m, i) => (
                <Line key={m} type="monotone" dataKey={m} stroke={`hsl(var(--chart-${(i % 5) + 1}))`} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-16 text-center">
            <LineIcon className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 text-sm text-muted-foreground">No yield data yet. Log a yield to see the trend.</p>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        {logs.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="yields-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Metric</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead>Income</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((y) => (
                  <TableRow key={y.id} data-testid={`yield-row-${y.id}`}>
                    <TableCell className="font-mono-nums text-sm">{y.date}</TableCell>
                    <TableCell className="font-medium">{y.metric}</TableCell>
                    <TableCell className="text-right font-mono-nums font-semibold">{y.value} {y.unit}</TableCell>
                    <TableCell className="text-right font-mono-nums">{y.unit_price ? `${settings.currency_symbol}${Number(y.unit_price).toFixed(2)}` : "—"}</TableCell>
                    <TableCell data-testid={`yield-income-status-${y.id}`}>{y.unit_price ? <Badge variant={y.income_tx_id ? "default" : "secondary"}>{y.income_tx_id ? `Added ${fmt(y.value * y.unit_price)}` : "Pending weekly update"}</Badge> : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{y.notes || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(y.id)} data-testid={`delete-yield-${y.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <Badge variant="secondary" className="mb-2">No entries</Badge>
            <p className="text-sm text-muted-foreground">Start logging production for {active.name}.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
